import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, ShieldCheck, Quote, FileText, HeartPulse } from "lucide-react";
import { DOCTOR } from "../data/content";

const COMPROMISOS = [
  {
    title: "Comunicación sincera y objetiva",
    text: "Para que el tratamiento tenga éxito, el paciente deberá ser sincero y descriptivo en el relato de sus síntomas, más que interpretativo. La comunicación entre médico y paciente es fundamental para un diagnóstico acertado y el diseño del tratamiento correcto.",
  },
  {
    title: "Tiempo y paciencia en el tratamiento",
    text: "No existe ningún medicamento prodigioso o milagroso. Todo esquema requiere tiempo, dosis específica e individualizada para mostrar respuesta y mejoría. ¡Sea paciente!",
  },
  {
    title: "Cada enfermo es único",
    text: "'No hay enfermedades, lo que hay son enfermos' – Claude Bernard. Cada persona siente el dolor y su sufrimiento de manera personal. La dolencia es muy subjetiva.",
  },
  {
    title: "Respuesta variable al tratamiento",
    text: "La respuesta depende de: cronicidad, estadio, tratamientos previos y severidad de síntomas. La mayoría de casos requiere ajustes. Si abandona el tratamiento, los síntomas retornarán y la recuperación será más lenta.",
  },
  {
    title: "Efectos adversos: transparencia total",
    text: "Todo medicamento tiene efectos indeseables. Algunos pueden causar reacciones alérgicas por hipersensibilidad individual. Le informaré de manera clara antes de iniciar cualquier tratamiento.",
  },
  {
    title: "Precaución con fuentes no profesionales",
    text: "No le dé mucha importancia a información de envases, diccionarios de medicamentos o literatura de farmacéuticas. Pueden ser exageradas, parciales o causar confusión innecesaria.",
  },
  {
    title: "Diagnóstico responsable",
    text: "No inventaré ninguna enfermedad. Mi compromiso es indagar mediante examen clínico y escrutinio, con auxiliares diagnósticos, para un tratamiento con objetivos claros basado en evidencias y en más de 23 años de experiencia clínica.",
  },
  {
    title: "No excesos farmacológicos",
    text: "No receto chochos, pócimas, venenos ni drogas nocivas. No me excedo en medicamentos innecesarios. Solo utilizo los estrictamente necesarios para aliviar, controlar, estabilizar o curar.",
  },
  {
    title: "Soporte telefónico permanente",
    text: "Mientras sea mi paciente y siga mis indicaciones médicas, me comprometo a brindarle soporte telefónico permanente.",
  },
];

const DERECHOS = [
  "Recibir atención médica integral adecuada, cálida y de calidad",
  "Recibir trato digno y respetuoso",
  "Recibir información suficiente, clara, oportuna y veraz",
  "Decidir libremente sobre su atención médica, después de ser informado",
  "Otorgar o no su consentimiento válidamente informado",
  "Ser tratado con confidencialidad",
  "Obtener una segunda opinión, una vez bien informado",
  "Recibir atención médica en caso de urgencia",
  "Contar con un expediente clínico",
  "Ser atendido cuando se inconforme por la atención médica recibida",
];

export default function Compromisos() {
  return (
    <div className="max-w-5xl mx-auto px-4 py-12 md:py-16">
      <Link to="/" className="inline-flex items-center gap-2 text-slate-500 hover:text-cyan-700 transition mb-8 text-sm">
        <ArrowLeft size={16} /> Volver al inicio
      </Link>

      <div className="text-center mb-12">
        <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-cyan-50 text-cyan-700 text-sm font-medium mb-4">
          <ShieldCheck size={16} /> Responsabilidad y Compromiso
        </div>
        <h1 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
          Tratamiento Médico Psiquiátrico
        </h1>
        <p className="text-xl text-slate-600">Farmacológico y Psicoterapéutico</p>
      </div>

      <div className="bg-gradient-to-br from-cyan-50 to-teal-50 rounded-2xl p-8 mb-12 border border-cyan-100">
        <Quote className="text-cyan-600 mb-4" size={32} />
        <p className="text-lg italic text-slate-800 leading-relaxed">
          <strong>Primum non nocere</strong> — Al medicar: ¡Primero no dañar!
        </p>
        <p className="text-sm text-slate-600 mt-2">— Hipócrates, 345 a.C.</p>
        <p className="text-sm text-slate-600 mt-2 italic">
          "Las decisiones médicas siempre son instruidas y equilibradas, aun cuando éstas sean aparentemente muy sencillas."
        </p>
      </div>

      <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-3">
        <FileText className="text-cyan-700" size={26} />
        Consideraciones y aclaraciones
      </h2>

      <div className="space-y-5 mb-14">
        {COMPROMISOS.map((c, i) => (
          <div key={i} className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm hover:shadow-md transition-all">
            <div className="flex items-start gap-4">
              <div className="w-9 h-9 flex-shrink-0 rounded-full bg-gradient-to-br from-cyan-600 to-teal-600 text-white flex items-center justify-center font-bold text-sm">
                {i + 1}
              </div>
              <div>
                <h3 className="font-bold text-slate-900 text-lg mb-2">{c.title}</h3>
                <p className="text-slate-600 leading-relaxed">{c.text}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="bg-gradient-to-br from-slate-900 to-slate-800 text-white rounded-2xl p-8 md:p-10 shadow-xl">
        <div className="flex items-center gap-3 mb-6">
          <HeartPulse className="text-cyan-400" size={28} />
          <h2 className="text-2xl md:text-3xl font-bold">Carta de los Derechos Generales de los Pacientes</h2>
        </div>
        <p className="text-slate-300 mb-6">Como profesional de la medicina, sigo los lineamientos y normas oficiales vigentes:</p>
        <div className="grid md:grid-cols-2 gap-4">
          {DERECHOS.map((d, i) => (
            <div key={i} className="flex items-start gap-3 bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="w-7 h-7 flex-shrink-0 rounded-full bg-cyan-500 text-slate-900 flex items-center justify-center font-bold text-sm">
                {i + 1}
              </div>
              <span className="text-sm leading-relaxed">{d}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-12 text-center">
        <Quote className="inline-block text-cyan-600 mb-3" size={28} />
        <p className="text-lg italic text-slate-700 max-w-2xl mx-auto">
          "Soy humano, y nada de lo humano me es ajeno."
        </p>
        <p className="text-sm text-slate-500 mt-2">— Publio Terencio, Filósofo greco-romano, 200 a.C.</p>
        <p className="text-sm font-semibold text-slate-700 mt-5">{DOCTOR.name}</p>
      </div>
    </div>
  );
}
